import { useMemo } from 'react';
import { getDaysInMonth, getLunarKey, solarToLunar } from '../utils/lunarUtils';

export const getTimezoneOffsetHours = (date) => {
  if (!(date instanceof Date)) return 7;
  const offset = -date.getTimezoneOffset() / 60;
  return Number.isFinite(offset) ? offset : 7;
};

export default function useLunarMonthData({
  currentDate,
  holidays,
  holidayLookup,
  timezoneOffsetHours: timezoneOverride
}) {
  const timezoneOffsetHours = useMemo(
    () => (Number.isFinite(timezoneOverride)
      ? timezoneOverride
      : getTimezoneOffsetHours(currentDate)),
    [currentDate, timezoneOverride]
  );

  const monthData = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentDate);
    const result = [];
    const lunarKeyMap = new Map();

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const lunar = solarToLunar(date, timezoneOffsetHours);
      const solarKey = `${date.getMonth() + 1}-${date.getDate()}`;
      const lunarKey = getLunarKey(lunar);
      const legacyLunarKey = getLunarKey(lunar, { includeLeap: false });
      const holidayList = [
        ...(holidayLookup.solar.get(solarKey) || []),
        ...(holidayLookup.lunar.get(lunarKey) || []),
        ...(holidayLookup.lunar.get(legacyLunarKey) || [])
      ];

      const lunarEntries = lunarKeyMap.get(lunarKey) || [];
      lunarEntries.push({ date, lunar });
      lunarKeyMap.set(lunarKey, lunarEntries);

      if (legacyLunarKey !== lunarKey) {
        const legacyEntries = lunarKeyMap.get(legacyLunarKey) || [];
        legacyEntries.push({ date, lunar });
        lunarKeyMap.set(legacyLunarKey, legacyEntries);
      }

      result.push({ date, lunar, holidays: holidayList });
    }

    return { days: result, lunarKeyMap };
  }, [currentDate, holidayLookup, timezoneOffsetHours]);

  const monthDataMap = useMemo(() => {
    const map = new Map();
    monthData.days.forEach(entry => {
      map.set(entry.date.toDateString(), entry);
    });
    return map;
  }, [monthData]);

  const monthlyHolidays = useMemo(() => {
    if (!holidays.length) return [];

    const year = currentDate.getFullYear();
    const monthIndex = currentDate.getMonth();
    const items = [];
    const seen = new Set();
    const daysInMonth = getDaysInMonth(currentDate);

    holidays.forEach((holiday) => {
      if (holiday.type === 'solar' && holiday.solarDate) {
        const [month, day] = holiday.solarDate.split('-').map(Number);
        if (month === monthIndex + 1 && day >= 1 && day <= daysInMonth) {
          const date = new Date(year, monthIndex, day);
          const lunar = solarToLunar(date, timezoneOffsetHours);
          const key = `${holiday.code || holiday._id || holiday.name_vi}-${date.toDateString()}`;

          if (!seen.has(key)) {
            items.push({
              ...holiday,
              date,
              solarDisplay: `${day}/${month}`,
              lunarDisplay: `${lunar.day}/${lunar.month}`
            });
            seen.add(key);
          }
        }
      }

      if (holiday.type === 'lunar' && holiday.lunarDate) {
        const matches = monthData.lunarKeyMap.get(holiday.lunarDate) || [];

        matches.forEach(({ date, lunar }) => {
          const key = `${holiday.code || holiday._id || holiday.name_vi}-${date.toDateString()}`;

          if (!seen.has(key)) {
            items.push({
              ...holiday,
              date,
              solarDisplay: `${date.getDate()}/${monthIndex + 1}`,
              lunarDisplay: `${lunar.day}/${lunar.month}`
            });
            seen.add(key);
          }
        });
      }
    });

    return items.sort((a, b) => a.date - b.date);
  }, [currentDate, holidays, monthData.lunarKeyMap, timezoneOffsetHours]);

  return { monthData, monthDataMap, monthlyHolidays, timezoneOffsetHours };
}
